import App from 'antd/lib/app';
import Modal from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Checkbox from 'antd/lib/checkbox';
import Tabs from 'antd/lib/tabs';
import Tooltip from 'antd/lib/tooltip';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import React, { useCallback, useState } from 'react';

import { RecentPjmRegistrationUploadMeterDataRequestsList } from './RecentPjmRegistrationUploadMeterDataRequestsList';
import { Import, useImportContext } from '../../../../../components/import';
import { ParsedRecord } from '../../../../../components/import/ImportParser';
import {
    PjmRegistrationMeterDataUploadCsvColumns,
    pjmRegistrationMeterDataUploadParser,
    registrationMeterDataUploadCsvConfig,
} from '../../../../../domain/pjm/meter-data-upload/pjmRegistrationMeterDataUploadParser';
import { usePjmRegistrationsListQuery } from '../../../../../domain/pjm/queries';
import { PRIMARY_COLOR } from '../../../../../theme';
import { usePjmMeterDataUploadCreateMutation } from '../../../../../domain/pjm/meter-data-upload/queries';

interface IPjmRegistrationUploadMeterDataModalProps {
    onClose: (actionTaken: boolean) => void;
}

const PjmRegistrationUploadMeterDataModalWrapped = ({ onClose }: IPjmRegistrationUploadMeterDataModalProps) => {
    const { notification } = App.useApp();

    const {
        viewState: { view, records, file },
        setImportProgress,
        importHasErrors,
    } = useImportContext();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isCalculateWinterPeakLoad, setIsCalculateWinterPeakLoad] = useState<boolean>(false);

    const { mutateAsync: createMeterDataUpload } = usePjmMeterDataUploadCreateMutation();

    const handleImportSubmit = useCallback(
        async (records: ParsedRecord<PjmRegistrationMeterDataUploadCsvColumns>[]) => {
            if (!records.length || !file) return;

            let total = records.length;
            setImportProgress({ imported: 0, failed: 0, total });

            setIsLoading(true);

            try {
                await createMeterDataUpload({ file, is_calculate_winter_peak_load: isCalculateWinterPeakLoad });

                setImportProgress({ imported: records.length, failed: 0, total });

                notification.info({
                    key: 'upload-meter-data-file-info',
                    message: 'Meter Data file uploaded!',
                });
            } catch (err: any) {
                setImportProgress({ imported: 0, failed: total, total });

                notification.info({
                    key: 'upload-meter-data-file-error',
                    message: 'Cannot upload Meter Data file!',
                });
            }

            setIsLoading(false);
        },
        [file, isCalculateWinterPeakLoad]
    );

    const handleImportCancel = () => {
        const actionTaken = !!records.length;
        onClose(actionTaken);
    };

    const modalActions: JSX.Element[] = [
        ...(importHasErrors ? [<Import.DownloadErrorsButton key="upload-meter-data-download-errors" />] : []),
        <Button key="upload-meter-data-cancel-button" disabled={isLoading} onClick={handleImportCancel}>
            Close
        </Button>,
        <Import.SubmitButton<PjmRegistrationMeterDataUploadCsvColumns> key="upload-meter-data-submit-button" onClick={handleImportSubmit} />,
    ];

    const initModalTabs = [
        {
            key: '1',
            title: (
                <div>
                    Recent Uploads
                    <Tooltip title="Displays up to 10 most recent uploads. Refreshes every 30 seconds.">
                        <InfoCircleOutlined style={{ color: PRIMARY_COLOR, fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
            ),
            content: <RecentPjmRegistrationUploadMeterDataRequestsList />,
        },
        {
            key: '2',
            title: 'CSV format description',
            content: <Import.FileDescription showTitle={false} />,
        },
    ];

    return (
        <Modal
            title="Upload Meter Data"
            width="60%"
            open
            keyboard={false}
            destroyOnHidden
            maskClosable={false}
            onCancel={handleImportCancel}
            footer={modalActions}
        >
            <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {view === 'initial' && <Import.UploadZone acceptFileType=".csv" />}
                    {view === 'initial' && <Import.ImportTemplate templateName="upload-pjm-meter-data-template" />}
                    {['preview', 'importing', 'completed'].includes(view) && <Import.Preview<PjmRegistrationMeterDataUploadCsvColumns> />}
                    {
                        <Checkbox
                            style={{ padding: '10px 0 10px 0' }}
                            checked={isCalculateWinterPeakLoad}
                            onClick={() => setIsCalculateWinterPeakLoad(!isCalculateWinterPeakLoad)}
                        >
                            Calculate Winter Peak Load
                        </Checkbox>
                    }
                    {view === 'initial' && (
                        <Tabs
                            defaultActiveKey="1"
                            tabBarStyle={{ marginBottom: '0px' }}
                            items={initModalTabs.map(tabInfo => {
                                return {
                                    key: tabInfo.key,
                                    label: tabInfo.title,
                                    children: tabInfo.content,
                                };
                            })}
                        />
                    )}
                    {['importing', 'completed'].includes(view) && <Import.Progress />}
                </Space>
            </div>
        </Modal>
    );
};

export const PjmRegistrationUploadMeterDataModal = (props: IPjmRegistrationUploadMeterDataModalProps) => {
    const { notification } = App.useApp();

    const {
        data: registrations,
        isError,
        error,
    } = usePjmRegistrationsListQuery({
        filter: { status: 'NEW' },
        pagination: { pageSize: 1000 },
        include: 'location',
    });

    if (isError) {
        notification.error({ key: 'fetch-pjm-registrations-error', message: error.message || 'Cannot fetch PJM Registrations for validation!' });
    }

    return (
        <Import.Provider<PjmRegistrationMeterDataUploadCsvColumns>
            csvConfig={registrationMeterDataUploadCsvConfig}
            parser={pjmRegistrationMeterDataUploadParser(registrations?.data || [])}
        >
            <PjmRegistrationUploadMeterDataModalWrapped {...props} />
        </Import.Provider>
    );
};
